<template>
  <div class="v2-page">
    <v2-order-detail
      ref="orderDetailPage"
      :is-shipping="isShipping"
      :shipment-order="shipmentOrder"
      @ship-now="onClickShipNow"
      @cancel-create-shipment="handleCancelShipment"
      @create-shipment="handleConfirmShipment"
      @open-import-export="handleOpenImportExport"
    />

    <!-- Shipment Settings Modal -->
    <v2-modal
      :show="!!shipmentOrder"
      title="Shipment Settings"
      size="full"
      @close="handleCancelShipment"
    >
      <v2-order-detail
        v-if="shipmentOrder"
        :key="shipmentOrderKey"
        :shipment-order="shipmentOrder"
        :is-shipping="isShipping"
        @cancel-create-shipment="handleCancelShipment"
        @create-shipment="handleConfirmShipment"
        @open-import-export="handleOpenImportExport"
      />
    </v2-modal>

    <!-- Credit Limit Warning Modal -->
    <v2-modal
      :show="showCreditLimitModal"
      title="Create Shipment Warning"
      @close="showCreditLimitModal = false"
    >
      <p style="white-space: pre-line;">
        {{ createShipmentWarningMessage }}
      </p>
    </v2-modal>

    <!-- Quantity Validation Error Modal -->
    <v2-modal
      :show="showQuantityErrorModal"
      title="Quantity exceeds ordered amount"
      @close="showQuantityErrorModal = false"
    >
      <div class="v2-order-wrapper__error-summary">
        <v2-badge variant="destructive">
          {{ quantityErrors.length }}
        </v2-badge>
        <span>item(s) have quantities exceeding ordered amount.</span>
      </div>
      <div class="v2-order-wrapper__error-table">
        <table class="v2-order-wrapper__table">
          <thead>
            <tr>
              <th>SKU</th>
              <th class="v2-text-center">
                Requested
              </th>
              <th class="v2-text-center">
                Available order quantity
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(error, index) in displayedQuantityErrors"
              :key="index"
            >
              <td>
                <strong>{{ error.sku }}</strong>
              </td>
              <td class="v2-text-center">
                <v2-badge variant="destructive">
                  {{ error.requested }}
                </v2-badge>
              </td>
              <td class="v2-text-center">
                <v2-badge variant="success">
                  {{ error.available }}
                </v2-badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="quantityErrors.length > MAX_DISPLAYED_ERRORS"
        class="v2-text-center v2-mt-2"
      >
        <span
          v-if="!showAllErrors"
          class="v2-text-muted"
        >
          and {{ remainingErrorsCount }} more item(s)...
        </span>
        <button
          class="v2-order-wrapper__toggle-btn"
          @click="showAllErrors = !showAllErrors"
        >
          {{ showAllErrors ? 'Show Less' : 'Show All' }}
        </button>
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showQuantityErrorModal = false"
        >
          Close
        </v2-button>
      </template>
    </v2-modal>

    <!-- Shipment Import/Export Modal -->
    <v2-modal
      :show="showImportExportModal"
      title="Shipment Import/Export"
      @close="handleCloseImportExport"
    >
      <p>
        Download the shipment template, fill in the quantities and upload
      </p>
      <v2-button
        variant="primary"
        :disabled="isExportingShipment"
        @click="exportShipment"
      >
        {{ isExportingShipment ? 'Exporting...' : 'Export Shipment' }}
      </v2-button>
      <div class="v2-order-wrapper__upload-area v2-mt-4">
        <template v-if="!shipmentImportFile">
          <label
            class="v2-order-wrapper__upload-label"
            @click="$refs.shipmentFileInput.click()"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="v2-order-wrapper__upload-icon"
            >
              <polyline points="16 16 12 12 8 16" />
              <line
                x1="12"
                y1="12"
                x2="12"
                y2="21"
              />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
            <strong>Drag & Drop</strong>
            <span>or click to select files from your system</span>
          </label>
        </template>
        <div
          v-else
          class="v2-order-wrapper__file-info"
        >
          <span>{{ shipmentImportFile.name }}</span>
          <span class="v2-text-muted">{{ getSizeInKb(shipmentImportFile.size) }} KB</span>
          <v2-button
            variant="ghost"
            size="sm"
            @click="handleRemoveFile"
          >
            Remove
          </v2-button>
        </div>
        <input
          ref="shipmentFileInput"
          type="file"
          accept=".xlsx, .xls"
          style="display: none;"
          @change="onShipmentFileChange($event)"
        >
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="handleCloseImportExport"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isImportingShipment || !shipmentImportFile"
          @click="handleImportShipment"
        >
          {{ isImportingShipment ? 'Importing...' : 'Import' }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import { useShipmentCreation } from '@/composables/useShipmentCreation'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import V2OrderDetail from './V2OrderDetail.vue'

export default {
  name: 'V2Order',
  components: {
    V2Button,
    V2Badge,
    V2Modal,
    V2OrderDetail,
  },
  mixins: [UserRoleMixin],
  setup() {
    const shipment = useShipmentCreation()
    return { ...shipment }
  },
  data() {
    return {
      showCreditLimitModal: false,
      showQuantityErrorModal: false,
      showImportExportModal: false,
    }
  },
  methods: {
    processShipmentResult(result) {
      if (!result) return
      if (result.showQuantityErrorModal) {
        this.showQuantityErrorModal = true
      }
      if (result.showCreditLimitModal) {
        this.showCreditLimitModal = true
      }
      if (result.success) {
        this.shipmentOrder = null
        this.$refs.orderDetailPage?.loadOrder(this.$route.params.orderId)
      }
    },
    handleConfirmShipment() {
      const result = this.confirmShipment()
      if (result && typeof result.then === 'function') {
        result.then(res => this.processShipmentResult(res))
        return
      }
      this.processShipmentResult(result)
    },
    handleCancelShipment() {
      if (this.isImportExportOpen) return
      this.shipmentOrder = null
    },
    handleOpenImportExport() {
      this.openImportExportModal()
      this.showImportExportModal = true
    },
    handleCloseImportExport() {
      this.closeImportExportModal()
      this.showImportExportModal = false
    },
    handleRemoveFile() {
      this.removeShipmentFile(this.$refs.shipmentFileInput)
    },
    async handleImportShipment() {
      const result = await this.importShipment()
      if (result?.success) {
        this.showImportExportModal = false
        this.handleRemoveFile()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-order-wrapper {
  &__error-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__error-table {
    max-height: 280px;
    overflow-y: auto;
    border-radius: 0.5rem;
    border: 1px solid var(--v2-border);
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;

    th {
      padding: 0.5rem 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      background: var(--v2-muted);
      border-bottom: 1px solid var(--v2-border);
    }

    td {
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--v2-border);
      vertical-align: middle;
    }
  }

  &__toggle-btn {
    background: none;
    border: none;
    color: var(--v2-primary);
    cursor: pointer;
    font-size: 0.8125rem;
    padding: 0.25rem 0.5rem;

    &:hover {
      text-decoration: underline;
    }
  }

  &__upload-area {
    border: 2px dashed var(--v2-border);
    border-radius: 0.625rem;
    padding: 1.5rem;
  }

  &__upload-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    text-align: center;
    color: var(--v2-muted-foreground);
  }

  &__upload-icon {
    color: var(--v2-primary);
  }

  &__file-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.875rem;
  }
}
</style>
